// Offline storage utilities for PWA functionality

class OfflineStorage {
  constructor() {
    this.dbName = 'FamilyPlannerDB';
    this.version = 1;
    this.db = null;
    this.init();
  }

  async init() {
    if (!('indexedDB' in window)) {
      console.warn('IndexedDB not supported');
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = (event) => {
        console.error('IndexedDB error:', event.target.error);
        reject(request.error);
      };
      request.onsuccess = () => {
        this.db = request.result;
        
        // Add error handler for the database
        this.db.onerror = (event) => {
          console.error('Database error:', event.target.error);
        };
        
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object stores for different data types
        if (!db.objectStoreNames.contains('pendingSync')) {
          const pendingStore = db.createObjectStore('pendingSync', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          pendingStore.createIndex('type', 'type', { unique: false });
          pendingStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('cachedData')) {
          const cachedStore = db.createObjectStore('cachedData', { 
            keyPath: 'key' 
          });
          cachedStore.createIndex('type', 'type', { unique: false });
          cachedStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('offlineActions')) {
          const actionsStore = db.createObjectStore('offlineActions', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          actionsStore.createIndex('type', 'type', { unique: false });
          actionsStore.createIndex('status', 'status', { unique: false });
        }
      };
    });
  }

  // Cache data for offline access
  async cacheData(key, data, type = 'general') {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['cachedData'], 'readwrite');
    const store = transaction.objectStore('cachedData');
    
    const cacheItem = {
      key,
      data,
      type,
      timestamp: Date.now()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(cacheItem);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Retrieve cached data
  async getCachedData(key) {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['cachedData'], 'readonly');
    const store = transaction.objectStore('cachedData');

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => reject(request.error);
    });
  }

  // Store action for later sync
  async storeOfflineAction(action) {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['offlineActions'], 'readwrite');
    const store = transaction.objectStore('offlineActions');
    
    const actionItem = {
      ...action,
      timestamp: Date.now(),
      status: 'pending'
    };

    return new Promise((resolve, reject) => {
      const request = store.add(actionItem);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Get pending actions for sync
  async getPendingActions() {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['offlineActions'], 'readonly');
    const store = transaction.objectStore('offlineActions');
    const index = store.index('status');

    return new Promise((resolve, reject) => {
      const request = index.getAll('pending');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Mark action as synced
  async markActionSynced(id) {
    if (!this.db) await this.init();

    const transaction = this.db.transaction(['offlineActions'], 'readwrite');
    const store = transaction.objectStore('offlineActions');

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);
      getRequest.onsuccess = () => {
        const action = getRequest.result;
        if (action) {
          action.status = 'synced';
          action.syncedAt = Date.now();
          const putRequest = store.put(action);
          putRequest.onsuccess = () => resolve(putRequest.result);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(null);
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  // Clear old cached data
  async clearOldCache(olderThan = 7 * 24 * 60 * 60 * 1000) { // 7 days
    if (!this.db) await this.init();

    const cutoff = Date.now() - olderThan;
    const transaction = this.db.transaction(['cachedData'], 'readwrite');
    const store = transaction.objectStore('cachedData');
    const index = store.index('timestamp');

    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(cutoff));
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Check if online
  isOnline() {
    return navigator.onLine;
  }

  // Queue action for sync when online
  async queueForSync(action) {
    if (this.isOnline()) {
      // If online, try to sync immediately
      try {
        await this.syncAction(action);
        return true;
      } catch (error) {
        console.error('Failed to sync action immediately:', error);
        // Fall through to offline storage
      }
    }

    // Store for later sync
    await this.storeOfflineAction(action);
    
    // Register for background sync if available
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('background-sync');
      } catch (error) {
        console.error('Failed to register background sync:', error);
      }
    }

    return false; // Indicates action was queued, not immediately synced
  }

  // Sync individual action (to be implemented per action type)
  async syncAction(action) {
    switch (action.type) {
      case 'shopping-list-item':
        return this.syncShoppingListItem(action);
      case 'calendar-event':
        return this.syncCalendarEvent(action);
      case 'message':
        return this.syncMessage(action);
      case 'todo-item':
        return this.syncTodoItem(action);
      default:
        throw new Error(`Unknown action type: ${action.type}`);
    }
  }

  // Specific sync methods (to be implemented based on your API)
  async syncShoppingListItem(action) {
    // Implementation depends on your shopping list API
    console.log('Syncing shopping list item:', action);
  }

  async syncCalendarEvent(action) {
    // Implementation depends on your calendar API
    console.log('Syncing calendar event:', action);
  }

  async syncMessage(action) {
    // Implementation depends on your messaging API
    console.log('Syncing message:', action);
  }

  async syncTodoItem(action) {
    // Implementation depends on your todo API
    console.log('Syncing todo item:', action);
  }

  // Sync all pending actions
  async syncAllPending() {
    if (!this.isOnline()) {
      console.log('Cannot sync while offline');
      return;
    }

    const pendingActions = await this.getPendingActions();
    const results = [];

    for (const action of pendingActions) {
      try {
        await this.syncAction(action);
        await this.markActionSynced(action.id);
        results.push({ action, success: true });
      } catch (error) {
        console.error('Failed to sync action:', action, error);
        results.push({ action, success: false, error });
      }
    }

    return results;
  }
}

// Create and export singleton instance
const offlineStorage = new OfflineStorage();

export default offlineStorage;

// Utility functions for common operations
export const cacheUserData = (userData) => {
  return offlineStorage.cacheData('userData', userData, 'user');
};

export const getCachedUserData = () => {
  return offlineStorage.getCachedData('userData');
};

export const cacheShoppingLists = (lists) => {
  return offlineStorage.cacheData('shoppingLists', lists, 'shopping');
};

export const getCachedShoppingLists = () => {
  return offlineStorage.getCachedData('shoppingLists');
};

export const cacheCalendarEvents = (events) => {
  return offlineStorage.cacheData('calendarEvents', events, 'calendar');
};

export const getCachedCalendarEvents = () => {
  return offlineStorage.getCachedData('calendarEvents');
};

export const addOfflineAction = (type, data) => {
  return offlineStorage.queueForSync({
    type,
    data,
    timestamp: Date.now()
  });
};